import { isPortal } from "../portal";
import { tabletWidth } from "../layout";
import { BotSpeed, PlayerMap, State } from "./types";
import {
  ANONYMOUS_USER_NAME,
  Card,
  ModalID,
  NOCARD,
  Player,
  Score,
  SlamState,
  stateDescription,
  TurnState,
} from "../constants";
import {combineWithAnd, getCardAction, getPrettyCardDesc, sum} from "../utils";
import { t, nm, presetName } from "../i18n";
import sortBy from "lodash/sortBy";
import isUndefined from "lodash/isUndefined";
import { areGameRoundTurnsOver } from "./gameSelectors";
import { PlayerStatus, RuleSet, RulePreset, ruleSetOf, presetOf, scoreRound, cardScore as commonCardScore } from "../common";

export const getHandForPlayer = (state: State, playerID: string) => {
  return state.gameState?.hands ? state.gameState?.hands[playerID] : [];
};

export const getGameState = (state: State) => state.gameState;
export const getRoomCode = (state: State) => state.roomCode;
export const getTurnState = (state: State) => state?.gameState?.turnState;
export const getSlamState = (state: State) => state?.gameState?.slam?.slamState;
export const getOwner = (state: State) => state.owner;
export const getSelf = (state: State) => state.self;
export const getSelectedCards = (state: State) => state.selectedCards;

export const getPlayerMap = (state: State) => state.players;

export const getPlayers = (state: State) => {
  const result: PlayerMap = {};
  // Rooms load in stages (and older rooms may lack leftPlayerIds): never spread a missing list.
  // Spreading undefined threw "(intermediate value) is not iterable" and sent players home.
  const combinedPlayerIds = [...(state?.gameState?.allPlayerIds || []), ...(state?.gameState?.leftPlayerIds || [])];
  const bots = state?.gameState?.bots || {};
  combinedPlayerIds.forEach((pid) => {
    if (pid in bots && bots[pid]) {
      result[pid] = bots[pid] as Player;
    } else {
      const typed = state?.gameState?.names?.[pid];
      if (typed) {
        result[pid] = { ...(state?.players?.[pid] || {}), id: pid, name: typed } as Player;
      } else if (state?.players?.[pid]?.name) {
        result[pid] = state.players[pid];
      }
    }
  });
  return result;
};

function snackbarSwapWithOpponent(previousPlayer: Player, self: string, opponent?: Player) {
  if (previousPlayer?.id === self) {
    return t("msg.youSwappedWith", { name: nm(opponent?.name) });
  } else if (opponent?.id === self) {
    return t("msg.swappedWithYou", { name: nm(previousPlayer?.name) });
  } else {
    return t("msg.swappedWith", { name: nm(previousPlayer?.name), other: nm(opponent?.name) });
  }
}

export const snackbarSlamMessage = (state: State) => {
  const slamState = getSlamState(state);
  const mySlam = isMySlam(state);
  const slamPlayer = getSlamPlayer(state);
  if (slamState === SlamState.SlamStart) {
    if (mySlam) {
      return getSlamRule(state) === "own"
        ? t("msg.youSlamOwn")
        : t("msg.youSlam");
    } else {
      return t("msg.choseToSlam", { name: nm(slamPlayer?.name) });
    }
  }

  if (slamState === SlamState.SlamSuccessOpponent) {
    if (mySlam) {
      // TODO opponent name
      return t("msg.yourSlamCorrectPick");
    } else {
      return t("msg.slamCorrect", { name: nm(slamPlayer?.name) });
    }
  }
  if (slamState === SlamState.SlamTimeout) {
    if (mySlam) {
      return t("msg.youSlamTooLong");
    } else {
      return t("msg.slamTooLong", { name: nm(slamPlayer?.name) });
    }
  }
};

export const snackbarSlamFinishMessage = (state: State) => {
  const slamState = getSlamState(state);
  const mySlam = isMySlam(state);
  const slamPlayer = getSlamPlayer(state);

  if (
    slamState === SlamState.SlamFailureOwn ||
    slamState === SlamState.SlamFinishFailureOwn ||
    slamState === SlamState.SlamFailureOpponent ||
    slamState === SlamState.SlamFinishFailureOpponent
  ) {
    if (mySlam) {
      return t("msg.youSlammedWrong");
    } else {
      return t("msg.slamWrong", { name: nm(slamPlayer?.name) });
    }
  }

  if (slamState === SlamState.SlamFinishSuccessOwn || slamState === SlamState.SlamFinishSuccessOpponent) {
    if (mySlam) {
      return t("msg.youSlammedRight");
    } else {
      return t("msg.slammedRight", { name: nm(slamPlayer?.name) });
    }
  }
};

export const snackbarPreviousTurn = (state: State) => {
  const previousPlayer = getPreviousPlayer(state);
  const previousTurn = state?.gameState?.previousTurn;
  const players = getPlayers(state);

  if (previousTurn?.turnState === TurnState.Timeout) {
    if (state?.gameState?.playerRafiki) {
      if (previousPlayer?.id === state?.self) {
        return t("msg.youTimedOutPenalty");
      } else {
        return t("msg.timedOutPenalty", { name: nm(previousPlayer?.name) });
      }
    }
    if (previousPlayer?.id === state?.self) {
      return t("msg.youTimedOut");
    } else {
      return t("msg.timedOut", { name: nm(previousPlayer?.name) });
    }
  }

  if (previousTurn?.turnState === TurnState.PlayerLeft) {
    if (previousPlayer?.name) {
      return t("msg.left", { name: nm(previousPlayer?.name) });
    }
    return t("msg.aPlayerLeft");
  }

  if (previousTurn?.turnState === TurnState.RemoveInactive) {
    const playerIds = previousTurn?.playerIds || [];
    let playerNames = playerIds?.map((playerId: string) => {
      const player = players?.[playerId];
      return nm(player?.name);
    });

    if (previousTurn?.playerIds?.includes(state?.self)) {
      return t("msg.youRemoved");
    } else if (playerNames.length === 1) {
      return t("msg.removedOne", { names: combineWithAnd(playerNames) });
    } else if (playerNames.length > 1) {
      return t("msg.removedMany", { names: combineWithAnd(playerNames) });
    }
  }

  if (previousTurn?.turnState === TurnState.NoValidMove) {
    if (previousPlayer?.id === state?.self) {
      return t("msg.youNoMove");
    } else {
      return t("msg.noMove", { name: nm(previousPlayer?.name) });
    }
  }
  if (previousTurn?.turnState === TurnState.Draw && previousPlayer?.id) {
    // Gringo: the drawn card was put straight on the discard pile
    if (previousPlayer?.id === state?.self) {
      return t("msg.youDiscardedDrawn");
    } else {
      return t("msg.discardedDrawn", { name: nm(previousPlayer?.name) });
    }
  }
  if (previousTurn?.turnState === TurnState.Replace) {
    if (previousPlayer?.id === state?.self) {
      return t("msg.youReplaced");
    } else {
      return t("msg.replaced", { name: nm(previousPlayer?.name) });
    }
  }
  if (previousTurn?.turnState === TurnState.LookAtOwn) {
    if (previousTurn?.player === state?.self) {
      const cards = getHandForPlayer(state, state?.self);
      const name = getPrettyCardDesc(cards?.[previousTurn?.playerIndex]);
      if (!name) return;
      return t("msg.youPeeked");
    } else {
      return t("msg.peeked", { name: nm(previousPlayer?.name) });
    }
  }
  if (previousTurn?.turnState === TurnState.LookAtOpponents) {
    const opponent = players?.[previousTurn.opponent];
    if (previousTurn.opponent === state?.self) {
      return t("msg.spiedOnYou", { name: nm(previousPlayer?.name) });
    } else if (previousPlayer?.id === state?.self) {
      return t("msg.youSpied", { name: nm(opponent?.name) });
    } else {
      return t("msg.spied", { name: nm(previousPlayer?.name), other: nm(opponent?.name) });
    }
  }

  if (previousTurn?.turnState === TurnState.SwapWithOpponent) {
    const opponent = players?.[previousTurn.opponent];
    return snackbarSwapWithOpponent(previousPlayer, state?.self, opponent);
  }
};

export const snackbarBeforeTurnMessage = (state: State) => {
  const currentPlayer = getCurrentPlayer(state);
  const turnState = getTurnState(state);

  if (areRoundTurnsOver(state)) {
    return "";
  }
  if (!isEveryoneReady(state)) {
    return t("msg.waitingReady");
  }
  if (isMyTurn(state)) {
    if (turnState === TurnState.Draw) {
      return t("msg.yourTurnDraw");
    }
  } else if (currentPlayer?.name) {
    return t("msg.theirTurn", { name: nm(currentPlayer.name) });
  } else {
    return "";
  }
};

export const snackbarTurnMessage = (state: State) => {
  const turnState = getTurnState(state);
  const players = getPlayers(state);

  if (isMyTurn(state)) {
    if (turnState === TurnState.Drawn) {
      const draw = state?.gameState?.drawnCard;
      if ([TurnState.LookAtOwn, TurnState.LookAtOpponents, TurnState.SwapWithOpponent].includes(getCardAction(draw, state?.gameState?.rules))) {
        return t("msg.drewPower", { card: getPrettyCardDesc(draw) });
      }
      return t("msg.drew", { card: getPrettyCardDesc(draw) });
    }
    if (turnState === TurnState.LookingAtOwn && !isUndefined(state?.gameState?.lookingAtIndex)) {
      const cards = getHandForPlayer(state, state?.self);
      const name = getPrettyCardDesc(cards?.[state?.gameState?.lookingAtIndex]);
      if (!name) return;
      return t("msg.youHave", { card: name });
    }
    if (
      turnState === TurnState.LookingAtOpponents &&
      !isUndefined(state?.gameState?.lookingAtIndex) &&
      !isUndefined(state?.gameState?.lookingAtPlayer)
    ) {
      const opponent = players?.[state?.gameState?.lookingAtPlayer];

      const cards = getHandForPlayer(state, opponent?.id);
      const name = getPrettyCardDesc(cards?.[state?.gameState?.lookingAtIndex]);
      return t("msg.theyHave", { name: nm(opponent?.name), card: name });
    }
    return stateDescription(state?.gameState?.turnState);
  } else {
    return "";
  }
};

export const getSnackbarMessage = (state: State) => {
  const previousPlayer = getPreviousPlayer(state);
  const myTurn = isMyTurn(state);
  const turnState = getTurnState(state);
  const playerRafiki = getRafikiPlayer(state);
  const previousTurn = state?.gameState?.previousTurn;
  const currentPlayer = getCurrentPlayer(state);
  const mySlam = isMySlam(state);

  if (myTurn && state?.showTimeoutWarning) {
    return t("msg.clockTicking");
  }

  if (mySlam && state?.showSlamTimeoutWarning) {
    return t("msg.clockTicking");
  }

  if (gameNotStarted(state)) {
    if (isOwner(state)) {
      return t("msg.startNew");
    }
    const owner = getRoomOwner(state);
    if (!owner?.name) return "";
    return t("msg.waitingOwner", { name: owner.name === ANONYMOUS_USER_NAME ? t("common.theRoomOwner") : nm(owner.name) });
  }

  const gameOver = isGameOver(state);
  if (gameOver && state?.gameState?.roundSaved) {
    const winners = getGameWinners(state);
    if (winners?.length === 1) {
      if (winners?.[0]?.id === state?.self) {
        return t("msg.youWonGameShort");
      } else {
        return t("msg.wonGame", { name: nm(winners?.[0]?.name) });
      }
    } else if (winners?.length && winners?.length > 1) {
      const iWon = winners.some((p) => p?.id === state?.self);
      const otherWinners = winners.filter((p) => p?.id !== state?.self);
      if (iWon) {
        return t("msg.youTied", { names: combineWithAnd(otherWinners.map((p) => nm(p?.name))) });
      } else {
        return t("msg.tied", { names: combineWithAnd(winners.map((p) => nm(p?.name))) });
      }
    }
  }

  if (turnState === TurnState.RoundOver && state?.gameState?.roundSaved) {
    const winners = getRoundWinners(state);
    if (winners?.length === 1) {
      if (winners?.[0]?.id === state?.self) {
        return t("msg.youWonRound");
      } else {
        return t("msg.wonRound", { name: nm(winners?.[0]?.name) });
      }
    } else if (winners?.length && winners?.length > 1) {
      const iWon = winners.some((p) => p?.id === state?.self);
      const otherWinners = winners.filter((p) => p?.id !== state?.self);
      if (iWon) {
        return t("msg.youTied", { names: combineWithAnd(otherWinners.map((p) => nm(p?.name))) });
      } else {
        return t("msg.tied", { names: combineWithAnd(winners.map((p) => nm(p?.name))) });
      }
    }
  }

  if (previousTurn?.turnState === TurnState.PlayerLeft) {
    if (previousTurn.player === state?.owner) {
      return t("msg.ownerLeft");
    }
  }

  if (isRoundOver(state)) {
    return t("msg.revealed");
  }

  if (showViewTwoModal(state)) {
    if (amIReady(state)) {
      return t("msg.waitingOthers");
    }
    return t("msg.rememberTwo");
  }

  if (previousTurn?.turnState === TurnState.CallRafiki && turnState === TurnState.Draw) {
    if (previousPlayer?.id === state?.self) {
      return t("msg.youCalled", { call: getCallWord(state) });
    } else if (myTurn) {
      return t("msg.yourLastTurn", { call: getCallWord(state), name: nm(playerRafiki) });
    } else {
      return t("msg.theirLastTurn", { call: getCallWord(state), name: nm(playerRafiki), player: nm(currentPlayer?.name) });
    }
  }

  const slamMessage = snackbarSlamMessage(state);
  const slamFinishMessage = snackbarSlamFinishMessage(state);
  const previousTurnSentence = snackbarPreviousTurn(state);
  const beforeTurnSentence = snackbarBeforeTurnMessage(state);
  const turnSentence = snackbarTurnMessage(state);

  if (slamFinishMessage && beforeTurnSentence) {
    return t("msg.twoSentences", { a: slamFinishMessage, b: beforeTurnSentence });
  }
  if (slamMessage) {
    return slamMessage;
  }
  if (previousTurnSentence && beforeTurnSentence) {
    return t("msg.twoSentences", { a: previousTurnSentence, b: beforeTurnSentence });
  }
  if (beforeTurnSentence) {
    return `${beforeTurnSentence}`;
  }
  if (turnSentence) {
    return turnSentence;
  }
  return `${turnSentence}`;
};

// Cards are turned red to highlight them
export const isCardBlack = (state: State, selectedCardsLength: number, card: Card) => {
  const playerID = card.playerID;
  const currentPlayer = getCurrentPlayer(state);
  if (isRoundOver(state)) {
    return true;
  }

  if (state.gameState.turn === -1) return true;

  if (playerID === "drawn") {
    return true;
  }

  if (playerID === "modal" || playerID === "discard") return true;

  const turnState = state?.gameState?.turnState;
  const slamState = state?.gameState?.slam?.slamState;
  const mySlam = isMySlam(state);
  const previousTurn = state?.gameState?.previousTurn;

  if (playerID === "draw") return true;

  if (slamState === SlamState.SlamStart) {
    if (mySlam && card.value in getAlreadySlammed(state)) {
      return false;
    }
    return true;
  }

  if (state?.gameState?.slam?.slamState === SlamState.SlamFailureOwn) {
    return !(card.index === state?.gameState?.slam?.slammedCardIndex && playerID === state?.gameState?.slam?.player);
  }
  if (
      state?.gameState?.slam?.slamState === SlamState.SlamFinishFailureOpponent && card.index === state?.gameState?.slam?.penaltyIndex
  ) {
    return false;
  }
  if (
      state?.gameState?.slam?.slamState === SlamState.SlamFinishFailureOwn && card.index === state?.gameState?.slam?.penaltyIndex
  ) {
    return false;
  }
  if (
    state?.gameState?.slam?.slamState === SlamState.SlamFailureOpponent ||
    state?.gameState?.slam?.slamState === SlamState.SlamSuccessOpponent
  ) {
    return !(card.index === state?.gameState?.slam?.slammedCardIndex && playerID === state?.gameState?.slam?.opponent);
  }

  if (turnState === TurnState.Draw) {
    if (state?.gameState?.slam?.slamState === SlamState.SlamFinishFailureOwn) {
      return !(card.index === state?.gameState?.slam?.slammedCardIndex && playerID === state?.gameState?.slam?.player);
    }
    if (
      state?.gameState?.slam?.slamState === SlamState.SlamFinishSuccessOpponent ||
      state?.gameState?.slam?.slamState === SlamState.SlamFinishFailureOpponent
    ) {
      return !(
        card.index === state?.gameState?.slam?.slammedCardIndex && playerID === state?.gameState?.slam?.opponent
      );
    }
    if (state?.gameState?.slam?.slamState === SlamState.SlamFinishSuccessOwn) {
      return true;
    }

    if (previousTurn?.turnState === TurnState.Replace) {
      return previousTurn?.playerIndex !== card.index || previousTurn?.player !== card.playerID;
    }
    if (previousTurn?.turnState === TurnState.LookAtOpponents) {
      return previousTurn?.opponentIndex !== card.index || previousTurn?.opponent !== card.playerID;
    }
    if (previousTurn?.turnState === TurnState.LookAtOwn) {
      return previousTurn?.playerIndex !== card.index || previousTurn?.player !== card.playerID;
    }
    if (previousTurn?.turnState === TurnState.SwapWithOpponent) {
      return (
        (previousTurn?.playerIndex !== card.index || previousTurn?.player !== card.playerID) &&
        (previousTurn?.opponentIndex !== card.index || previousTurn?.opponent !== card.playerID)
      );
    }
    return true;
  }
  if (currentPlayer?.id === state?.self) {
    const selectedCardPlayer = getPlayerFromCard(state, state?.selectedCards?.[0]?.value);
    if (turnState === TurnState.SwapWithOpponent && selectedCardsLength === 0) {
      return !["draw"].includes(playerID);
    }
    if (turnState === TurnState.SwapWithOpponent && selectedCardsLength === 1) {
      if (state?.selectedCards?.[0].value === card.value) {
        return false;
      }
      if (selectedCardPlayer?.id === state?.self) {
        return !["draw"].includes(playerID);
      } else {
        return true;
      }
    }
    return true;
  }
  return true;
};

export const shouldHideCard = (state: State, playerID: string, card: string, index?: number): boolean => {
  if (["discard", "drawn", "modal"].includes(playerID)) {
    return false;
  }
  // The start of a round: your two nearest cards lie face up on the table until you say
  // you're ready (the peek happens at the table, not in a pop-up).
  if (playerID === state?.self && (index === 2 || index === 3) && showViewTwoModal(state)) {
    return false;
  }
  // Daily Rafiki: the extra peek earned from a rewarded ad shows one more of your own cards.
  if (playerID === state?.self && typeof index === "number" && getExtraPeekIndex(state) === index && showViewTwoModal(state)) {
    return false;
  }
  const slamState = getSlamState(state);
  if (slamState === SlamState.SlamStart) {
    return true;
  }

  const turnState = getTurnState(state);
  const myTurn = isMyTurn(state);

  if (state?.gameState?.turnState === TurnState.RoundOver) {
    return playerID === "draw";
  }
  if (!myTurn) {
    return true;
  }
  if (turnState === TurnState.LookingAtOwn && state?.gameState?.lookingAtIndex === index && playerID === state?.self) {
    return false;
  }
  if (
    turnState === TurnState.LookingAtOpponents &&
    state?.gameState?.lookingAtIndex === index &&
    playerID === state?.gameState?.lookingAtPlayer
  ) {
    return false;
  }
  return true;
};

export const showDrawModal = (state: State) => {
  const turnState = getTurnState(state);
  const myTurn = isMyTurn(state);
  return (turnState === TurnState.Drawn || turnState === TurnState.Replace) && myTurn;
};

export const getDrawAndDiscard = (state: State) => {
  const gameState = state.gameState || {};
  return {
    draw: gameState?.deck?.length ? gameState?.deck[gameState?.deck?.length - 1] : "",
    discard: gameState?.discardPile?.length ? gameState?.discardPile[gameState?.discardPile?.length - 1] : "",
  };
};

export const isOwner = (state: State) => {
  return state.owner === state.self && state.owner;
};

export const isMyTurn = (state: State) => {
  return state?.gameState?.turn >= 0 && state?.gameState?.playerIds[state.gameState.turn] === state.self;
};

export const getDraw = (state: State) => {
  return state?.gameState?.drawnCard;
};

export const isModalOpen = (state: State, modalID: ModalID) => {
  return state?.modals[modalID];
};

export const getOpenModalId = (state: State) => {
  if (showSlamModal(state)) {
    return ModalID.SlamModal;
  }
  if (showViewTwoModal(state)) {
    return ModalID.ViewTwoModal;
  }
  if (showRafikiModal(state)) {
    return ModalID.CallRafikiModal;
  }
  if (showRoundOverModal(state)) {
    return ModalID.RoundOverModal;
  }
  if (isModalOpen(state, ModalID.SlammingInfoModal)) {
    return ModalID.SlammingInfoModal;
  }
  if (isModalOpen(state, ModalID.SlammingInfoOpponentModal)) {
    return ModalID.SlammingInfoOpponentModal;
  }
  if (isModalOpen(state, ModalID.RemovePlayersModal)) {
    return ModalID.RemovePlayersModal;
  }
  if (gamePaused(state)) {
    return ModalID.PauseModal;
  }
  if (showApproveModal(state)) {
    return ModalID.ApproveModal;
  }
  if (showRoomFullModal(state)) {
    return ModalID.RoomFullModal;
  }
  if (isDenied(state)) {
    return ModalID.DeniedModal;
  }
  if (isModalOpen(state, ModalID.PassOwnershipModal)) {
    return ModalID.PassOwnershipModal;
  }
  if (isModalOpen(state, ModalID.JoinGameModal)) {
    return ModalID.JoinGameModal;
  }
  return;
};

// The card the rewarded extra peek shows, if one was earned for this room's current round.
export const getExtraPeekIndex = (state: State): number | undefined => {
  const peek = state?.extraPeek;
  if (!peek || peek.roomCode !== state?.roomCode || peek.round !== state?.gameState?.round) return undefined;
  if (state?.gameState?.mode !== "daily") return undefined;
  return peek.index;
};

// Which card an extra peek would show: the first of your own cards that is still face down
// during the peek (the peek already shows slots 2 and 3).
export const getExtraPeekCandidate = (state: State): number | undefined => {
  const hand: string[] = state?.gameState?.hands?.[state?.self] || [];
  const i = hand.findIndex((c, idx) => Boolean(c) && idx !== 2 && idx !== 3);
  return i >= 0 ? i : undefined;
};

export const getBottomTwoCards = (state: State) => {
  const self = state.self;
  const myHand = state?.gameState?.hands?.[self];
  return [myHand?.[2], myHand?.[3]];
};

export const isSelected = (state: State, card: string) => state.selectedCards?.some((c) => c.value === card);

export const getCurrentPlayer = (state: State) => {
  const players = getPlayers(state);
  const result = players[state?.gameState?.playerIds[state?.gameState?.turn]];
  return result;
};

export const getPreviousPlayer = (state: State) => {
  // @ts-ignore
  const previousPlayerId = state?.gameState?.previousTurn && state?.gameState?.previousTurn?.player;
  const players = getPlayers(state);
  return players?.[previousPlayerId];
};

export const isNewGame = (state: State) => {
  return (
    state?.gameState?.turn === 0 &&
    state?.gameState?.deck?.length > 0 &&
    state?.gameState?.discardPile?.length === 0 &&
    state?.gameState?.loop === 0 &&
    state?.gameState?.turnState === TurnState.Draw
  );
};

export const getSlamDiscard = (state: State) => {
  return state?.gameState?.slam?.slamDiscard;
};

export const getSlamPlayer = (state: State) => {
  const players = getPlayers(state);
  const result = players?.[state?.gameState?.slam?.player];
  return result;
};

export const getSlamPlayerId = (state: State) => {
  const players = getPlayers(state);
  const result = players?.[state?.gameState?.slam?.player];
  return result?.id;
};

export const isSlamInitiated = (state: State) => {
  const slamState = getSlamState(state);
  return slamState === SlamState.SlamStart;
};

export const isMySlam = (state: State) => {
  const self = getSelf(state);
  return self === state?.gameState?.slam?.player;
};

export const areRoundTurnsOver = (state: State) => {
  return areGameRoundTurnsOver(state?.gameState);
};

export const isRoundOver = (state: State) => {
  return state?.gameState?.turnState === TurnState.RoundOver;
};

export const correctSlamDoneThisTurn = (state: State) => {
  return state?.gameState?.correctSlamDone;
};

const isSlamInProgress = (state: State) => {
  return [
    SlamState.SlamStart,
    SlamState.SlamFailureOwn,
    SlamState.SlamFailureOpponent,
    SlamState.SlamSuccessOpponent,
  ].includes(state?.gameState?.slam?.slamState);
};

export const canSlam = (state: State) => {
  const hand = state?.gameState?.hands?.[state?.self];
  const slamInProgress = isSlamInProgress(state);
  const tooManySlams = state?.gameState?.failedSlamCount?.[state?.self] >= 1;
  return (
    getSlamRule(state) !== "none" &&
    state?.gameState?.playerRafiki !== state?.self &&
    state?.gameState?.deck?.length > 0 &&
    !tooManySlams &&
    !slamInProgress &&
    state?.self !== state?.gameState?.playerRafiki &&
    state?.gameState?.turnState !== TurnState.Replace &&
    state?.gameState?.turnState !== TurnState.Drawn &&
    state?.gameState?.turnState !== TurnState.LookingAtOwn &&
    state?.gameState?.turnState !== TurnState.LookingAtOpponents &&
    !gameNotStarted(state) &&
    !gamePaused(state) &&
    hand?.some((c) => c !== NOCARD) &&
    state?.gameState?.discardPile.length > 0 &&
    !correctSlamDoneThisTurn(state) &&
    !isRoundOver(state) &&
    !isGameOver(state)
  );
};

export const getAlreadySlammed = (state: State) => {
  return state?.gameState?.slammedCards;
};

export const canCallRafiki = (state: State) => {
  const turnState = getTurnState(state);
  const myTurn = isMyTurn(state);
  const rafikiCalled = isRafikiCalled(state);
  const hasNotStarted = gameNotStarted(state);
  const roundTurnsOver = areRoundTurnsOver(state);
  const slamInProgress = isSlamInProgress(state);

  return (
    state?.gameState?.loop >= 3 &&
    !roundTurnsOver &&
    !hasNotStarted &&
    !rafikiCalled &&
    myTurn &&
    turnState === TurnState.Draw &&
    !gamePaused(state) &&
    !slamInProgress
  );
};

export const isRafikiCalled = (state: State) => {
  return Boolean(state?.gameState?.playerRafiki);
};

export const getRafikiPlayer = (state: State) => {
  const players = getPlayers(state);
  const result = players?.[state?.gameState?.playerRafiki];
  return result?.name;
};

export const getRafikiPlayerId = (state: State) => {
  return state?.gameState?.playerRafiki;
};

export const isMyRafiki = (state: State) => {
  return state?.gameState?.playerRafiki === state?.self;
};

export const showRafikiModal = (state: State) => {
  return (
    getRafikiPlayer(state) &&
    state?.modals[ModalID.CallRafikiModal] &&
    !areRoundTurnsOver(state) &&
    state?.gameState?.playerIds?.includes(state?.self)
  );
};

// Card values depend on the house rules ("rafiki" by default, or "gringo"); see common/index.ts.
export const cardScore = (card: string, rules?: string): number => commonCardScore(card, rules);

export const handScore = (hand: Array<string>, rules?: string): number => {
  if (!hand) {
    return 0;
  }
  const values = hand.map((card) => cardScore(card, rules));
  return sum(values);
};

export const getRules = (state: State): RuleSet => ruleSetOf(state?.gameState?.rules);
export const getPreset = (state: State): RulePreset => presetOf(state?.gameState?.rules);
// Which hands a player may slam from under the room's rules ("any", "own" or "none").
export const getSlamRule = (state: State) => getPreset(state).slam;
// What players shout to end the round: RAFIKI, or GRINGO, CAMBIO, CABO or KABOO under those house rules.
export const getCallWord = (state: State): string => getPreset(state).callWord;

export const getRoundScoreBoard = (state: State) => {
  const players = getPlayers(state);
  if (!players) {
    return;
  }
  // Score the players still at the table under the room's house rules (common/index.ts):
  // hand points, then the caller's penalty or bonus.
  const hands: { [id: string]: string[] } = {};
  state?.gameState?.playerIds.forEach((playerId) => {
    hands[playerId] = state?.gameState?.hands?.[playerId];
  });
  return scoreRound(hands, state?.gameState?.playerRafiki, state?.gameState?.rules).map(({ id, roundScore, penaltyAdded, bonusAdded }) => ({
    id,
    name: players[id]?.name,
    roundScore,
    penaltyAdded,
    // only Kaboo has a caller bonus; the usual scoreboard entries keep their shape
    ...(bonusAdded ? { bonusAdded } : {}),
  }));
};

export const getRoundWinners = (state: State) => {
  const scoreboard = getRoundScoreBoard(state);
  const topScore = scoreboard?.[0]?.roundScore;
  return scoreboard?.filter((player) => player?.roundScore === topScore);
};

export const getGameScores = (state: State) => {
  const scoreboard = getRoundScoreBoard(state);
  const scoreMap: any = {};
  // const players = getPlayers(state);
  scoreboard?.forEach((player) => {
    return (scoreMap[player?.id] = {
      ...player,
      score: 0,
    });
  });
  state?.scores.forEach(
    (player) =>
      (scoreMap[player?.id] = {
        ...scoreMap[player?.id],
        score: player?.score,
      })
  );
  return scoreMap;
};

export const getGameScoreboard = (state: State) => {
  const scoreMap = getGameScores(state);
  const players = getPlayers(state);
  const scores = sortBy(
    state?.gameState?.playerIds.map((playerId: string) => {
      const player = players[playerId];
      return {
        ...player,
        score: scoreMap[player?.id]?.score,
        roundScore: scoreMap[player?.id]?.roundScore,
      };
    }),
    "score"
  );
  return scores;
};

export const getGameWinners = (state: State) => {
  const scoreboard = getGameScoreboard(state);
  const topScore = scoreboard?.[0]?.score;
  return scoreboard?.filter((player) => player?.score === topScore);
};

export const showRoundOverModal = (state: State) => {
  return (
    state?.gameState?.turnState === TurnState.RoundOver &&
    state?.modals[ModalID.RoundOverModal] &&
    state?.gameState?.roundSaved
  );
};

export const isEveryoneReady = (state: State) => {
  return state?.gameState?.playerIds?.every((player) => state?.gameState?.ready?.[player]);
};

export const isPlayerReady = (state: State, playerId: string) => {
  return state?.gameState?.ready?.[playerId];
};

export const shouldViewCards = (state: State) => {
  return imApproved(state) && !isEveryoneReady(state) && !gamePaused(state) && state?.gameState?.turn >= 0;
};

export const showViewTwoModal = (state: State) => {
  return (
    shouldViewCards(state) &&
    state?.modals[ModalID.ViewTwoModal] &&
    !amIReady(state) &&
    state?.gameState?.playerIds?.includes(state?.self)
  );
};

export const gameNotStarted = (state: State) => {
  return state?.gameState?.turn === -1;
};

export const getPlayerFromCard = (state: State, card: string) => {
  const playerIds = Object.keys(state?.gameState?.hands);
  const players = getPlayers(state);
  for (const playerId of playerIds) {
    const hand = state?.gameState?.hands?.[playerId];
    if (hand.includes(card)) {
      return players?.[playerId];
    }
  }
};

export const getDebug = (state: State) => state?.debug;

export const getHumanPlayerCount = (state: State) => {
  // The people at the table: not every computer in the room is dealt in (a round of death).
  return state?.gameState?.playerIds?.filter(
    (player) => state?.gameState?.playerStatus?.[player] === PlayerStatus.Approved && !(player in (state?.gameState?.bots || {}))
  )?.length;
};

export const getPlayerCount = (state: State) => {
  return state?.gameState?.playerIds?.length;
};

export const getActivePlayerCount = (state: State) => {
  return state?.gameState?.playerIds?.filter((p) => state?.gameState?.isPlaying?.[p])?.length;
};

export const shouldCallAutomaticRafiki = (state: State) => {
  const hand = getHandForPlayer(state, state?.self);
  return hand?.every((card) => card === NOCARD) && isMyTurn(state) && !isRafikiCalled(state);
};

export const getRoomOwner = (state: State) => {
  const players = getPlayers(state);
  const result = players?.[state?.owner];
  return result;
};

export const getOwnPlayer = (state: State) => {
  const players = getPlayers(state);
  const result = players?.[state?.self];
  return result;
};

export const getPlayerIds = (state: State) => {
  return state?.gameState?.playerIds;
};

export const isRedSnackbar = (state: State) => {
  return isRafikiCalled(state) && !areRoundTurnsOver(state);
};

export const isYellowSnackbar = (state: State) => {
  return isWaitingOnMe(state) && !areRoundTurnsOver(state);
};

export const canPauseGame = (state: State) => {
  return isOwner(state) && !gameNotStarted(state);
};

export const gamePaused = (state: State) => {
  return state?.gameState?.paused;
};

// A game against the computers only (no other people at the table).
export const isComputersOnlyGame = (state: State) => {
  const gs: any = state?.gameState;
  const bots = Object.keys(gs?.bots || {});
  const others = (gs?.playerIds || []).filter((id: string) => id !== state?.self);
  return others.length > 0 && others.every((id: string) => bots.includes(id));
};

// The rules ("?") are open over a game against the computers: the game waits for the player
// (critic issue #102). The turn timer, the peek countdown and the computers all stop, and carry
// on where they were when the rules close. Games with other people aren't held up for everyone.
// Only where the rules cover the table: phones and tablets, and portals (where "?" is the only way
// in). On a wide website screen the rules sit beside the table, and a player may keep them open
// while playing, so the game goes on.
const rulesCoverTable = () => {
  try {
    return isPortal || window.innerWidth <= tabletWidth;
  } catch (e) {
    return false;
  }
};
export const isHelpPause = (state: State, covers: boolean = rulesCoverTable()) =>
  covers && Boolean(state?.sidebarOpen) && isComputersOnlyGame(state);

// Every clock in the game stands still: the game is paused, or the rules are open (above).
export const clocksStopped = (state: State) => Boolean(gamePaused(state)) || isHelpPause(state);

export const getCombinedPlayerIds = (state: State) => {
  return [...(state?.gameState?.allPlayerIds || []), ...(state?.gameState?.leftPlayerIds || [])];
};

export const getPlayerStatus = (state: State) => {
  return state?.gameState?.playerStatus;
};

export const showApproveModal = (state: State) => {
  return gameNotStarted(state) && isOwner(state) && isModalOpen(state, ModalID.ApproveModal);
};

// A player who is not the owner, at a table whose game has not started: they wait for the
// owner (who alone can start), and see who is there meanwhile.
export const showLobbyWaiting = (state: State) => {
  if (!gameNotStarted(state) || isOwner(state) || !state?.owner || !state?.self) return false;
  const status = state?.gameState?.playerStatus?.[state.self];
  return status === PlayerStatus.Approved || status === PlayerStatus.Pending;
};

export const getAllOtherHumanPlayers = (state: State) => {
  const players = getPlayers(state);
  return state?.gameState?.allPlayerIds
    .filter(
      (p) =>
        p !== state?.self &&
        state?.gameState?.playerStatus?.[p] !== PlayerStatus.Limbo &&
        !state?.gameState?.leftPlayerIds?.includes(p) &&
        state?.gameState?.isPlaying?.[p] &&
        !(p in (state?.gameState?.bots || {}))
    )
    .map((p) => players[p]);
};

export const isSidebarOpen = (state: State) => {
  return state?.sidebarOpen;
};

export const canTakeSlamPenalty = (state: State) => {
  return state?.gameState?.hands?.[state?.self]?.some((card) => card === NOCARD);
};

export const imApproved = (state: State) => {
  return state?.gameState?.playerStatus?.[state?.self] === PlayerStatus.Approved;
};

export const getBoardPlayers = (state: State) => {
  const players = getPlayers(state);
  return state?.gameState?.playerIds.map((p) => players[p]);
};

export const getTimestamp = (state: State) => state?.gameState?.timestamp;

export const shouldSaveRound = (state: State) => {
  const gameState = state.gameState;
  const lastTurn = areRoundTurnsOver(state);
  return lastTurn && !gameState.roundSaved && isOwner(state) && gameState?.turnState === TurnState.RoundOver;
};

export const isGameOver = (state: State) => {
  const lastRound = finishedLastRound(state);
  return lastRound && isRoundOver(state);
};

export const shouldUpdateLeaderBoard = (state: State) => {
  const gameState = state.gameState;
  return isGameOver(state) && !gameState.leaderBoardUpdated && isOwner(state) && gameState?.gameSaved;
};

export const shouldRecordGameStarted = (state: State) => {
  return !state?.gameState?.initialGameSaved && !gameNotStarted(state) && isOwner(state);
};

export const allApprovedOrDenied = (state: State) => {
  const gameState = state?.gameState;
  return gameState?.allPlayerIds?.every((p) => gameState?.playerStatus?.[p] !== PlayerStatus.Pending);
};

export const canStartGame = (state: State) => {
  return allApprovedOrDenied(state) || state?.gameState?.playerIds?.length === 6;
};

export const imTimedOut = (state: State) => {
  return !state?.gameState?.isPlaying?.[state?.self] && state?.gameState?.playerIds.includes(state?.self);
};

export const hasGameLoaded = (state: State) => {
  const players = getPlayers(state);
  return state?.gameState?.playerIds.every((player) => player in players);
};

export const havePlayerNamesLoaded = (state: State) => {
  const players = getPlayers(state);
  return state?.gameState?.allPlayerIds.length > 0 && state?.gameState?.allPlayerIds?.every((p) => p in players);
};

export const isRoomFull = (state: State) => {
  return state?.gameState?.playerIds?.length === 6;
};

export const showRoomFullModal = (state: State) => {
  return isRoomFull(state) && !state?.gameState?.playerIds?.includes(state?.self);
};

export const getPreviousTurn = (state: State) => {
  return state?.gameState?.previousTurn;
};

export const finishedLastRound = (state: State) => {
  return state?.gameState?.rounds - 1 <= state?.gameState?.round;
};

export const isLeaderBoardOpen = (state: State) => {
  return state?.leaderBoardOpen;
};

export const getIsPlaying = (state: State) => {
  return state?.gameState?.isPlaying;
};

export const isWaitingOnMe = (state: State) => {
  const slamState = getSlamState(state);
  if (isRoundOver(state)) {
    return false;
  }
  if (
    [
      SlamState.SlamStart,
      SlamState.SlamFailureOwn,
      SlamState.SlamFailureOpponent,
      SlamState.SlamSuccessOpponent,
    ].includes(slamState)
  ) {
    return state?.gameState?.slam?.player === state?.self;
  }
  return isMyTurn(state);
};

export const shouldStartSlamTimeout = (state: State) => {
  const slamState = getSlamState(state);
  if ([SlamState.SlamStart].includes(slamState)) {
    return state?.gameState?.slam?.player === state?.self;
  }
};

export const getSlammedCard = (state: State) => {
  if (state?.gameState?.slam?.slamState === SlamState.SlamFinishSuccessOwn) {
    return state?.gameState?.discardPile?.[state?.gameState?.discardPile?.length - 1];
  } else if (state?.gameState?.slam?.slamState === SlamState.SlamSuccessOpponent) {
    return state?.gameState?.discardPile?.[state?.gameState?.discardPile?.length - 1];
  } else if (state?.gameState?.slam?.slamState === SlamState.SlamFailureOwn) {
    return state?.gameState?.hands?.[state?.gameState?.slam?.player]?.[state?.gameState?.slam?.slammedCardIndex];
  } else if (state?.gameState?.slam?.slamState === SlamState.SlamFailureOpponent) {
    return state?.gameState?.hands?.[state?.gameState?.slam?.opponent]?.[state?.gameState?.slam?.slammedCardIndex];
  }
};

export const isFirstLoop = (state: State) => {
  return state?.gameState?.loop === 0;
};

export const showSlamModal = (state: State) => {
  const slamState = state?.gameState?.slam?.slamState;
  return (
    (slamState === SlamState.SlamSuccessOpponent ||
      slamState === SlamState.SlamFinishSuccessOwn ||
      slamState === SlamState.SlamFailureOwn ||
      slamState === SlamState.SlamFailureOpponent) &&
    isModalOpen(state, ModalID.SlamModal)
  );
};

export const isDenied = (state: State) => {
  return [PlayerStatus.Denied, PlayerStatus.Limbo].includes(state?.gameState?.playerStatus?.[state?.self]);
};

export const canRetryEntry = (state: State) => {
  return state?.gameState?.attempts?.[state?.self] < 3;
};

export const getRound = (state: State) => state.gameState?.round + 1;
export const getTotalRounds = (state: State) => state.gameState?.rounds;

export const getBots = (state: State) =>
  (state?.gameState?.playerIds || [])
    .filter((p) => p in (state?.gameState?.bots || {}))
    .map((p) => state?.gameState?.bots[p]);

export const amIReady = (state: State) => state.gameState?.ready?.[state?.self];

export const shouldShowTimeoutWarning = (state: State) => state?.showTimeoutWarning;

export const shouldShowSlamTimeoutWarning = (state: State) => state?.showSlamTimeoutWarning;
// When the slam window after the last discard closes (ms, local clock); see redux/slamWindow.ts.
export const getSlamWindowUntil = (state: State) => state?.slamWindowUntil || 0;

export const getSlamModalTitle = (state: State) => {
  const slamState = state?.gameState?.slam?.slamState;
  let title;
  if (slamState === SlamState.SlamFailureOwn || slamState === SlamState.SlamFailureOpponent) {
    title = t("slam.failTitle");
  } else if (slamState === SlamState.SlamSuccessOpponent || slamState === SlamState.SlamFinishSuccessOwn) {
    title = t("slam.correctTitle");
  }
  return title;
};

export const getSlamModalStrapLine = (state: State) => {
  const slamState = state?.gameState?.slam?.slamState;
  const playerSlam = getSlamPlayer(state);
  const mySlam = isMySlam(state);
  let strapLine;
  if (slamState === SlamState.SlamFailureOwn || slamState === SlamState.SlamFailureOpponent) {
    if (mySlam) {
      strapLine = t("slam.youWrong");
    } else {
      strapLine = t("slam.theyWrong", { name: nm(playerSlam?.name) });
    }
  } else if (slamState === SlamState.SlamFinishSuccessOwn) {
    if (mySlam) {
      strapLine = t("slam.youRight");
    } else {
      strapLine = t("slam.theyRight", { name: nm(playerSlam?.name) });
    }
  } else if (slamState === SlamState.SlamSuccessOpponent) {
    if (mySlam) {
      strapLine = t("slam.youRightPick");
    } else {
      strapLine = t("slam.theyRight", { name: nm(playerSlam.name) });
    }
  }
  return strapLine;
};

export const getRoundOverTitleAndStrapLine = (state: State) => {
  // @ts-ignore
  const roundWinners: Score[] = getRoundWinners(state);
  const self = getSelf(state);
  const rounds = getTotalRounds(state);
  const lastRound = finishedLastRound(state);
  const gameWinners: Score[] = getGameWinners(state);
  const tied = gameWinners && gameWinners?.length > 1;

  const gameOver = lastRound && !tied;
  const roundOrGame = gameOver ? "game" : "round";
  const winners = gameOver ? gameWinners : roundWinners;
  const iWon = winners?.some((p: any) => p?.id === self);
  const score = winners?.[0]?.roundScore;

  // One-round games (quick play, the portals) just say who won.
  const endOf = t("over.endOfN", { count: rounds });
  const gameTitle = (me: boolean) => (rounds === 1 ? (me ? t("over.youWon") : t("over.gameOver")) : endOf);
  const titles = {
    "one-winner": {
      me: {
        round: t("over.congrats"),
        game: gameTitle(true),
      },
      other: {
        round: t("over.roundOver"),
        game: gameTitle(false),
      },
    },
    tied: {
      me: {
        round: t("over.roundOver"),
        game: endOf,
      },
      other: {
        round: t("over.roundOver"),
        game: endOf,
      },
    },
  };
  const otherWinners = winners.filter((p) => p?.id !== self);
  const strapLines = {
    "one-winner": {
      me: {
        round: t("over.youWonRound", { score }),
        game: rounds === 1 ? t("over.lowestWinsYou", { score }) : t("msg.youWonGame"),
      },
      other: {
        round: t("over.wonRound", { name: nm(winners?.[0]?.name), score }),
        game: rounds === 1 ? t("over.winsWith", { name: nm(winners?.[0]?.name), score }) : t("over.hasWonGame", { name: nm(winners?.[0]?.name) }),
      },
    },
    tied: {
      me: {
        round: t("over.youTied", { names: combineWithAnd(otherWinners.map((p) => nm(p.name))), score }),
        game: t("over.tied", { names: combineWithAnd(winners.map((p) => nm(p.name))), score }), // This should never happen
      },
      other: {
        round: t("over.tied", { names: combineWithAnd(otherWinners.map((p) => nm(p.name))), score }),
        game: t("over.tied", { names: combineWithAnd(winners.map((p) => nm(p.name))), score }), // This should never happen
      },
    },
  };
  const noOfWinners = winners?.length === 1 ? "one-winner" : "tied";
  const who = iWon ? "me" : "other";

  const title = titles[noOfWinners][who][roundOrGame];
  const strapLine = strapLines[noOfWinners][who][roundOrGame];
  return {
    title,
    strapLine,
  };
};

export const isSlamPending = (state: State) => {
  return [
    SlamState.SlamStart,
    SlamState.SlamSuccessOpponent,
    SlamState.SlamFailureOwn,
    SlamState.SlamFailureOpponent,
  ].includes(state?.gameState?.slam?.slamState);
};

export const getPenaltyLine = (state: State) => {
  const scoreBoard = getRoundScoreBoard(state);
  const rafikiCallerScore = scoreBoard?.find((x) => x?.id === state?.gameState?.playerRafiki);
  const preset = getPreset(state);
  const mine = state?.self === state?.gameState?.playerRafiki;
  if (rafikiCallerScore?.bonusAdded) {
    // Kaboo: the caller takes points off for a successful call
    return mine
      ? t("over.youBonus", { n: preset.callerBonus, call: presetName(preset.id) })
      : t("over.bonus", { name: nm(rafikiCallerScore?.name), n: preset.callerBonus, call: presetName(preset.id) });
  }
  if (!rafikiCallerScore?.penaltyAdded) {
    return "";
  }
  return mine
    ? t("over.youPenalty", { n: preset.callerPenalty, call: presetName(preset.id) })
    : t("over.penalty", { name: nm(rafikiCallerScore?.name), n: preset.callerPenalty, call: presetName(preset.id) });
};

export const canSignOut = (state: State) => {
  if (isSlamPending(state) && state?.gameState?.slam?.player === state?.self) return false;
  if (isMyTurn(state)) return false;
};

export const getBoardCoordinates = (state: State) => {
  return state?.coordinates;
};

export const leaderboardIntersectingBoard = (state: State, lbCoords: DOMRect) => {
  const coordinates = getBoardCoordinates(state);
  if (!coordinates) return false;
  if (!lbCoords) return false;
  const x = (coordinates?.left + coordinates?.right) / 2;
  const y = (coordinates?.top + coordinates?.bottom) / 2;
  const radius = coordinates?.width / 2;

  const dist = Math.sqrt(Math.pow(lbCoords.right - x, 2) + Math.pow(lbCoords.bottom - y, 2));
  return dist < radius;
};

export const getBotSpeed = (state: State) => {
  return state?.gameState?.botSpeed || BotSpeed.Fast;
};

export const getLookingAtIndex = (state: State) => {
  return state?.gameState?.lookingAtIndex;
};

export const getLookingAtPlayer = (state: State) => {
  return state?.gameState?.lookingAtPlayer;
};

export const shouldResetDeck = (state: State) => {
  return (
    state?.gameState?.turnState === TurnState.Draw &&
    state?.gameState?.deck?.length === 0 &&
    !gameNotStarted(state) &&
    !gamePaused(state) &&
    !isRoundOver(state) &&
    !isGameOver(state)
  );
};

export const isUpdatingGameState = (state: State) => {
  return state?.isUpdatingGameState;
};

export const getModalOffset = (state: State) => {
  return state?.modalOffsets?.[ModalID.RoundOverModal];
};

export const getShouldShowRadar = (state: State, playerID: string) => {
  const currentPlayer = getCurrentPlayer(state);
  const turnState = getTurnState(state);
  if (turnState === TurnState.RoundOver) {
    return false;
  }
  return currentPlayer?.id === playerID;
};

export const isCardBright = (state: State, playerID: string): boolean => {
  const currentPlayer = getCurrentPlayer(state);
  if (["draw", "discard", "drawn", "modal"].includes(playerID)) {
    return true;
  }
  // Peeking at the start of a round: your own hand is the thing to look at.
  if (playerID === state?.self && showViewTwoModal(state)) {
    return true;
  }
  const slamState = getSlamState(state);
  const mySlam = isMySlam(state);
  if (mySlam && slamState === SlamState.SlamStart) {
    // Cambio: you can only stick a card from your own hand
    return getSlamRule(state) === "own" ? playerID === state?.self : true;
  }
  const turnState = getTurnState(state);
  const myTurn = isMyTurn(state);
  if (myTurn && [TurnState.LookAtOpponents, TurnState.LookingAtOpponents].includes(turnState)) {
    return currentPlayer?.id !== playerID;
  }
  if (myTurn && [TurnState.SwapWithOpponent].includes(turnState)) {
    return true;
  }
  if (myTurn && [TurnState.SwapBetweenOpponents].includes(turnState)) {
    return currentPlayer?.id !== playerID;
  }
  if (turnState === TurnState.RoundOver) {
    return true;
  }
  return currentPlayer?.id === playerID;
};

